import { useSaveUserProfile } from '@actions';
import { ReactComponent as ContactIcon } from '@assets/icons/profile-icons/contact-icon.svg';
import { ReactComponent as EditIcon } from '@assets/icons/profile-icons/edit-icon.svg';
import Button from '@components/V4/Button';
import IconButton from '@components/V4/IconButton';
import Input from '@components/V4/Inputs/Input';
import SkeletonBox from '@components/V4/SkeletonBox';
import { zodResolver } from '@hookform/resolvers/zod';
import useToast from 'apps/agora/src/hooks/useToast';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import { ProfileContext } from '../AccountProfile';

interface ProfileAsideLinkedinProps {
  linkedin: string;
  userId: string;
  hasEditPermissions: boolean;
}

const linkedinSchema = z.object({
  linkedin: z.string().trim().url({ message: 'Please enter a valid URL.' }).or(z.literal('')),
});

type FormData = z.infer<typeof linkedinSchema>;

const ProfileAsideLinkedin = (props: ProfileAsideLinkedinProps) => {
  const { linkedin, userId, hasEditPermissions } = props;
  const [isEditMode, setIsEditMode] = useState(false);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isLoading: isProfileLoading } = useContext(ProfileContext);

  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(linkedinSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: { linkedin },
  });

  const editButtonHandler = () => {
    setIsEditMode((prev) => !prev);
    reset({ linkedin });
  };

  const { mutate: updateUserProfile, isLoading } = useSaveUserProfile(userId, {
    onSuccess: async () => {
      toast.success('Successfully updated your profile.');
      await queryClient.invalidateQueries('/users/:id/profile');
      editButtonHandler();
    },
    onError: () => {
      toast.error('Profile could not be saved.');
    },
  });

  const onSubmit = (data: FormData) => {
    updateUserProfile({ linkedin: data.linkedin ?? '' });
  };

  return isEditMode ? (
    <div className="flex gap-6 py-4 bg-surfaceHover px-6 rounded-xl">
      <div className="flex flex-col gap-4 w-full">
        <div className="flex gap-4 w-full">
          <ContactIcon className="min-w-4 min-h-4 w-4 h-4 text-customPrimary" />
          <h3 className="font-raleway font-bold">LinkedIn</h3>
        </div>
        <div className="flex flex-col gap-6">
          <Input
            type="text"
            id="linkedin"
            label="LinkedIn URL"
            placeholder="https://www.linkedin.com/in/..."
            isTouched={touchedFields.linkedin}
            isValid={!errors.linkedin}
            errorText={errors.linkedin?.message}
            {...register('linkedin')}
          />
        </div>
        <div className="flex justify-end gap-4">
          <Button buttonText="Cancel" variant="secondary" onMouseDown={editButtonHandler} />
          <Button
            buttonText="Save"
            isLoading={isLoading}
            variant="primary"
            onMouseDown={handleSubmit(onSubmit)}
          />
        </div>
      </div>
    </div>
  ) : (
    <div className="flex gap-6 py-4 px-6">
      <div className="flex gap-4 w-full">
        <ContactIcon className="min-w-4 min-h-4 w-4 h-4 text-customPrimary" />
        <div className="flex flex-col gap-6 w-full">
          <h3 className="font-raleway font-bold">LinkedIn</h3>
          {isProfileLoading ? (
            <SkeletonBox className="w-2/3 h-6 rounded-lg" />
          ) : linkedin ? (
            <a
              href={linkedin}
              target="_blank"
              rel="noreferrer"
              className="text-customPrimary break-all hover:underline"
            >
              {linkedin}
            </a>
          ) : (
            <span className="text-customGrey text-sm">No LinkedIn added</span>
          )}
        </div>
      </div>
      {hasEditPermissions && (
        <IconButton
          onClick={editButtonHandler}
          className="min-h-6 min-w-6"
          icon={<EditIcon className="min-w-3 min-h-3" />}
        />
      )}
    </div>
  );
};

export default ProfileAsideLinkedin;
